import { defineRule, configure } from 'vee-validate'
import {
  required,
  email,
  min,
  max,
  min_value,
  max_value,
  numeric,
} from '@vee-validate/rules'

// Configure vee-validate
configure({
  generateMessage: (ctx) => {
    const messages: Record<string, string> = {
      required: `Le champ ${ctx.field} est requis`,
      email: `Le champ ${ctx.field} doit etre une adresse email valide`,
      min: `Le champ ${ctx.field} doit contenir au moins ${ctx.rule?.params} caracteres`,
      max: `Le champ ${ctx.field} ne doit pas depasser ${ctx.rule?.params} caracteres`,
      min_value: `Le champ ${ctx.field} doit etre superieur ou egal a ${ctx.rule?.params}`,
      max_value: `Le champ ${ctx.field} doit etre inferieur ou egal a ${ctx.rule?.params}`,
      numeric: `Le champ ${ctx.field} doit etre un nombre`,
      phone: `Le numero de telephone n'est pas valide`,
      password: `Le mot de passe doit contenir au moins 6 caracteres, une lettre et un chiffre`,
      safeText: `Le champ ${ctx.field} contient des caracteres non autorises`,
      safeAddress: `Le champ ${ctx.field} contient des caracteres non autorises`,
      safeCity: `Le champ ${ctx.field} contient des caracteres non autorises`,
      safeProfession: `Le champ ${ctx.field} contient des caracteres non autorises`,
      postalCode: `Le code postal n'est pas valide`,
    }

    return messages[ctx.rule?.name ?? ''] ?? `Le champ ${ctx.field} n'est pas valide`
  },
})

// Register built-in rules
defineRule('required', required)
defineRule('email', email)
defineRule('min', min)
defineRule('max', max)
defineRule('min_value', min_value)
defineRule('max_value', max_value)
defineRule('numeric', numeric)

// Custom password rule
defineRule('password', (value: string) => {
  if (!value) return true
  if (value.length < 6) return false
  if (value.length > 200) return false
  if (!/\d/.test(value)) return false
  if (!/[a-zA-Z]/.test(value)) return false
  if (/[^a-zA-Z0-9!@#$%^&*()_+]/.test(value)) return false
  return true
})

// Phone: digits, optional leading +, spaces/dashes/parens/dots between groups.
// Rejects anything else (emojis, letters, control chars).
defineRule('phone', (value: string) => {
  if (!value) return true
  if (value.length < 10) return false
  if (value.length > 20) return false
  if (!/^\+?[\d\s.\-()]+$/.test(value)) return false
  if (!/\d/.test(value)) return false
  return true
})

// Whitelist for human names / city / paroisse. Allows any Unicode letter
// (covers é, è, à, ç, Arabic letters, etc.), spaces, hyphens, apostrophes
// (straight + curly), and periods. Rejects digits, emojis, punctuation that
// doesn't appear in real names, and any control characters.
const SAFE_NAME_RE = /^[\p{L}\s.'\u2019\-]+$/u
defineRule('safeText', (value: string) => {
  if (!value) return true
  return SAFE_NAME_RE.test(value)
})

// Whitelist for postal addresses. Same as names plus digits, commas, slashes,
// and parentheses — still rejects emojis and pictographic characters.
const SAFE_ADDRESS_RE = /^[\p{L}\p{N}\s.,'\u2019\-()/]+$/u
defineRule('safeAddress', (value: string) => {
  if (!value) return true
  return SAFE_ADDRESS_RE.test(value)
})

// City names: same as safeText plus digits (FR arrondissements — "Paris 15").
const SAFE_CITY_RE = /^[\p{L}\p{N}\s.'\u2019\-]+$/u
defineRule('safeCity', (value: string) => {
  if (!value) return true
  return SAFE_CITY_RE.test(value)
})

// Profession: same as addresses plus "&" and "+" ("R&D", "Bac+5"). Mirrors
// auth.copte.fr exactly: whitespace runs collapsed and the value trimmed, then
// a 100-character cap (String.length) and SAFE_PROFESSION_RE. The cap lives
// here rather than in `max:100`, which counts the raw value in code points.
const SAFE_PROFESSION_RE = /^[\p{L}\p{N}\s.,'\u2019\-()/&+]+$/u
defineRule('safeProfession', (value: string, _params: unknown, ctx: { field: string }) => {
  const v = (value || '').replace(/\s+/g, ' ').trim()
  if (!v) return true
  if (v.length > 100) return `Le champ ${ctx.field} ne doit pas depasser 100 caracteres`
  return SAFE_PROFESSION_RE.test(v)
})

// Postal code: digits only, optional spaces/hyphens. Covers FR 5-digit codes
// and variants like Canadian "A1A 1A1" style (letters omitted on purpose
// since this project is France-only).
defineRule('postalCode', (value: string) => {
  if (!value) return true
  if (value.length < 3) return false
  if (value.length > 10) return false
  return /^[\d\s-]+$/.test(value)
})
